import { useState, useEffect, type ChangeEvent } from "react";
import { SectionBox, inputCls } from "../shared";
import { MOMENT_IMAGE_ALLOWED_SIZES } from "../../utils/imageValidator";
import ImagePickerArea from "./ImagePickerArea";

type Props = {
  body: string;
  messageTitle: string;
  imageName: string | null;
  landingPcUrl: string;
  landingMobileUrl: string;
  titleEnabled: boolean;
  onBodyChange: (value: string) => void;
  onMessageTitleChange: (value: string) => void;
  onImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onLibrarySelect?: (imgUrl: string) => void;
  onLandingChange: (key: "pc" | "mobile", value: string) => void;
  onTitleEnabledChange: (val: boolean) => void;
};

export default function WideMessageFields({
  body, messageTitle, imageName, landingPcUrl, landingMobileUrl,
  titleEnabled, onBodyChange, onMessageTitleChange, onImageChange,
  onLibrarySelect, onLandingChange, onTitleEnabledChange,
}: Props) {
  const [materialPcEnabled, setMaterialPcEnabled] = useState(landingPcUrl.trim().length > 0);

  useEffect(() => {
    setMaterialPcEnabled(landingPcUrl.trim().length > 0);
  }, [landingPcUrl]);

  const handleMaterialPcToggle = (checked: boolean) => {
    setMaterialPcEnabled(checked);
    if (!checked) onLandingChange("pc", "");
  };

  const handleTitleToggle = (val: boolean) => {
    onTitleEnabledChange(val);
    if (!val) onMessageTitleChange("");
  };

  return (
    <>
      {/* 소재 */}
      <SectionBox label="소재" required>
        <label className="tw-block tw-text-xs tw-text-gray-500">
          <span className="tw-text-red-500 tw-mr-1">*</span>
          홍보 이미지
        </label>
        <ImagePickerArea
          imageName={imageName}
          sizeHint="800×600px"
          allowedSizes={MOMENT_IMAGE_ALLOWED_SIZES.WIDE_MESSAGE}
          onFileChange={onImageChange}
          onLibrarySelect={onLibrarySelect}
        />
        <div>
          <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">
            <span className="tw-text-red-500 tw-mr-1">*</span>
            모바일 랜딩 URL
          </label>
          <input
            value={landingMobileUrl}
            onChange={(e) => onLandingChange("mobile", e.target.value)}
            placeholder="https://"
            className={inputCls}
          />
        </div>
        <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
          <input type="checkbox" checked={materialPcEnabled} onChange={(e) => handleMaterialPcToggle(e.target.checked)} className="tw-w-3.5 tw-h-3.5" />
          PC 링크
        </label>
        {materialPcEnabled && (
          <div>
            <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">PC 랜딩 URL</label>
            <input value={landingPcUrl} onChange={(e) => onLandingChange("pc", e.target.value)} placeholder="https://" className={inputCls} />
          </div>
        )}
      </SectionBox>

      {/* 타이틀 */}
      <SectionBox label="타이틀" enabled={titleEnabled} onToggle={handleTitleToggle}>
        <input
          value={messageTitle}
          onChange={(e) => onMessageTitleChange(e.target.value)}
          maxLength={20}
          placeholder="타이틀을 입력하세요."
          className={inputCls}
        />
        <p className="tw-text-xs tw-text-gray-400">{messageTitle.length} / 20</p>
      </SectionBox>

      {/* 홍보문구 */}
      <SectionBox label="홍보문구" required>
        <label className="tw-block tw-text-xs tw-text-gray-500">
          <span className="tw-text-red-500 tw-mr-1">*</span>
          홍보문구
        </label>
        <textarea
          rows={6}
          maxLength={76}
          value={body}
          onChange={(e) => {
            const val = e.target.value;
            if ((val.match(/\n/g) || []).length > 5) return;
            onBodyChange(val);
          }}
          placeholder="홍보문구를 입력하세요."
          className={`${inputCls} tw-resize-none`}
        />
        <div className="tw-flex tw-justify-end tw-gap-4 tw-text-xs tw-text-gray-400">
          <span>개행 {(body.match(/\n/g) || []).length} / 5</span>
          <span>{body.length} / 76</span>
        </div>
      </SectionBox>
    </>
  );
}
